import { useEffect } from 'react';

// App view has no sideways scrolling and shows little at a time: every data table inside `rootRef`
// is shown as a stacked card (appView.css, `table[data-stack]`). CSS can't read a column's header, so
// this copies each header text onto its cells as `data-label`, and keeps doing it as rows change.
// A card with more than SHOWN info cells shows the first SHOWN and folds the rest behind a tap
// ("Show all details"); cells holding a button, link or field (the actions) are never folded, nor are
// cells a page marks `data-keep`. A page can mark one cell `data-title` to be the card's bold heading.
// Tables without a header row (plain key/value tables) are left alone.
const SHOWN = 3;

export default function useStackTables(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    let queued = false;

    const run = () => {
      queued = false;
      root.querySelectorAll('table:not([data-no-stack])').forEach((table) => {
        const headRow = table.tHead?.rows[table.tHead.rows.length - 1];
        if (!headRow) return;
        let labels = null;
        table.querySelectorAll('tbody tr').forEach((row) => {
          const cells = Array.from(row.cells);
          const fresh = cells.filter((td) => !td.hasAttribute('data-label'));
          if (!fresh.length) return;
          if (!labels) labels = Array.from(headRow.cells).map((th) => th.textContent.trim());
          // A row with merged cells (e.g. "No records" colspan) keeps its own layout.
          const merged = cells.some((c) => c.colSpan > 1);
          fresh.forEach((td) => td.setAttribute('data-label', merged ? '' : (labels[td.cellIndex] || '')));
          if (merged) return;
          let info = 0;
          cells.forEach((td) => {
            const hasText = td.textContent.trim() !== '';
            const isAction = td.querySelector('button, a[href], input, select, textarea');
            if (!hasText && !isAction) return;
            if (isAction || td.hasAttribute('data-title') || td.hasAttribute('data-keep')) return;
            info += 1;
            if (info > SHOWN) td.setAttribute('data-extra', '');
          });
          if (info > SHOWN) row.setAttribute('data-more', '');
        });
        if (!table.hasAttribute('data-stack')) table.setAttribute('data-stack', '');
      });
    };
    const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(run); } };

    // Tap on the card's bottom strip (where "Show all details" is drawn) folds/unfolds it.
    const onClick = (e) => {
      const row = e.target.closest?.('tr[data-more]');
      if (!row || !root.contains(row) || e.target.closest('button, a, input, select, textarea, label')) return;
      const r = row.getBoundingClientRect();
      if (e.clientY < r.bottom - 40) return;
      e.stopPropagation();
      e.preventDefault();
      row.toggleAttribute('data-open');
    };

    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    root.addEventListener('click', onClick, true);
    schedule();
    return () => { observer.disconnect(); root.removeEventListener('click', onClick, true); };
  }, [rootRef]);
}
